import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { useQueryString } from "../../../Hooks/useQueryString";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { passwordValidator } from "../../../utils/passwordValidator";
import { Button, ButtonLoading } from "../../SharedComponents/Button/Button";
import "./LoginCompleteSetup.css";
import { LoginCompleteSetupCheckbox } from "./LoginCompleteSetupCheckbox/LoginCompleteSetupCheckbox";
import { LoginCompleteSetupInput } from "./LoginCompleteSetupInput/LoginCompleteSetupInput";

export const LoginCompleteSetup = () => {
  const { t, i18n } = useTranslation();
  const queryParams = useQueryString();
  const encodedEmail = queryParams.get("email");

  const history = useHistory();
  const [userCreating, setUserCreating] = useState(false);
  const [email] = useState(decodeURIComponent(encodedEmail));

  const [completeSetup] = useMutation(completeUserCreationMutation);

  const { data, loading, error } = useQuery(fetchUserInfo, {
    variables: { email },
  });

  const submitHandler = async ({ name, password }) => {
    setUserCreating(true);
    try {
      await completeSetup({
        variables: { name, email, password, lang: i18n.language },
      });

      setUserCreating(false);

      history.push(`/login/${email}`);
    } catch (err) {
      handleGraphQLErrors(err);
      setUserCreating(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <Redirect to="/login" />;
  }

  if (data?.user?.status === "ACTIVE") {
    return <Redirect to={"/login/" + data?.user?.email} />;
  }

  return (
    <div className="LoginCompleteSetup">
      <Formik
        initialValues={{
          name: data?.user?.name || "",
          password: "",
          confirmedPassword: "",
          licenseAccepted: false,
        }}
        onSubmit={({ name, password }) => {
          submitHandler({ name, password });
        }}
        validationSchema={validationSchema}
      >
        {({ dirty, values, isValid }) => (
          <Form>
            <LoginCompleteSetupInput
              name="name"
              type="text"
              label={t("generic.name")}
            />
            <LoginCompleteSetupInput
              name="password"
              type="password"
              label={t("generic.password")}
            />
            <LoginCompleteSetupInput
              name="confirmedPassword"
              type="password"
              label={t("loginForm.repeatPassword")}
            />

            <LoginCompleteSetupCheckbox
              name="licenseAccepted"
              type="checkbox"
              checked={values.licenseAccepted}
            />

            <div className="LoginCompleteSetup__submit">
              <div className="LoginCompleteSetup__submit-button">
                <Button
                  buttonStyle="Button__primary"
                  type="submit"
                  disabled={
                    userCreating ||
                    !dirty ||
                    !isValid ||
                    !values.licenseAccepted
                  }
                >
                  {userCreating ? <ButtonLoading /> : t("users.create")}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
  password: passwordValidator.required("validation.passwordRequired"),
  confirmedPassword: Yup.string().oneOf(
    [Yup.ref("password"), null],
    "loginForm.passwordNoMatch"
  ),
  licenseAccepted: Yup.boolean().required("errors.acceptLicense"),
});

const completeUserCreationMutation = gql`
  mutation respondToNewUserInvitation(
    $name: String
    $email: String
    $password: String
    $lang: String
  ) {
    respondToNewUserInvitation(
      name: $name
      email: $email
      password: $password
      lang: $lang
    )
  }
`;

const fetchUserInfo = gql`
  query user($email: String) {
    user(email: $email) {
      id
      name
      status
      email
    }
  }
`;
